import { useState, useEffect, useRef, useCallback } from "react";
import { perfumeAPI, pricesAPI, liveAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Delete,
  X,
  ShoppingCart,
  Monitor,
  Smartphone,
  User,
  Send,
  Maximize,
  Minimize,
  FlipHorizontal2,
  Sparkles,
  Type,
  Bell,
  Laugh,
  Volume2,
  DollarSign,
  Lock,
  Unlock,
  RotateCcw,
  Plus,
} from "lucide-react";

const FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];
const TEXT_DURATIONS = [5, 10, 15, 30, 60];

// Notification sound - gentle chime
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a gentle bell/chime sound
    const playTone = (freq, startTime, duration, volume = 0.3) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      
      // Soft attack and decay
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    
    // Play a pleasant two-tone chime (like a doorbell)
    playTone(880, now, 0.3, 0.2);        // A5
    playTone(1108, now + 0.15, 0.4, 0.15); // C#6
    playTone(1320, now + 0.3, 0.5, 0.1);  // E6
    
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Loud celebration bell sound (for orders)
const playBellSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    
    const playTone = (freq, startTime, duration, volume = 0.5) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    // Loud celebratory bell sequence
    playTone(1047, now, 0.8, 0.6);       // C6
    playTone(1319, now + 0.2, 0.8, 0.5); // E6
    playTone(1568, now + 0.4, 0.8, 0.5); // G6
    playTone(2093, now + 0.6, 1.2, 0.4); // C7 - long
    playTone(1568, now + 1.0, 0.6, 0.3); // G6
    playTone(2093, now + 1.3, 1.0, 0.4); // C7 final
    
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Laughter sound effect - Real human laughter audio
const playLaughterSound = () => {
  try {
    // Use a real laughter audio file from a public CDN
    const audio = new Audio('https://www.soundjay.com/human/sounds/laughter-1.mp3');
    audio.volume = 0.7;
    audio.play().catch(() => {
      // Fallback to synthesized laughter if audio fails
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const now = audioContext.currentTime;
      
      // Create more realistic "ha ha ha" with voice-like qualities
      const createVoiceLaugh = (startTime, pitch, duration, vol) => {
        // Main voice oscillator
        const osc1 = audioContext.createOscillator();
        const osc2 = audioContext.createOscillator();
        const noise = audioContext.createBufferSource();
        const gainNode = audioContext.createGain();
        const filter = audioContext.createBiquadFilter();
        
        // Create noise buffer for breathiness
        const bufferSize = audioContext.sampleRate * duration;
        const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          output[i] = Math.random() * 2 - 1;
        }
        noise.buffer = noiseBuffer;
        
        // Connect nodes
        osc1.connect(filter);
        osc2.connect(filter);
        noise.connect(gainNode);
        filter.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Voice-like settings
        osc1.type = 'sawtooth';
        osc2.type = 'triangle';
        filter.type = 'bandpass';
        filter.frequency.value = 1200;
        filter.Q.value = 2;
        
        // Pitch variation for natural feel
        osc1.frequency.setValueAtTime(pitch, startTime);
        osc1.frequency.linearRampToValueAtTime(pitch * 0.7, startTime + duration * 0.8);
        osc2.frequency.setValueAtTime(pitch * 2, startTime);
        osc2.frequency.linearRampToValueAtTime(pitch * 1.5, startTime + duration);
        
        // Volume envelope - quick attack, natural decay
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(vol, startTime + 0.02);
        gainNode.gain.setValueAtTime(vol * 0.8, startTime + duration * 0.3);
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
        
        osc1.start(startTime);
        osc1.stop(startTime + duration);
        osc2.start(startTime);
        osc2.stop(startTime + duration);
        noise.start(startTime);
        noise.stop(startTime + duration);
      };
      
      // Sequence of laughs - like a group laughing
      createVoiceLaugh(now, 220, 0.15, 0.25);        // ha
      createVoiceLaugh(now + 0.12, 240, 0.12, 0.3);  // ha
      createVoiceLaugh(now + 0.22, 200, 0.18, 0.35); // HA
      createVoiceLaugh(now + 0.38, 260, 0.1, 0.25);  // ha
      createVoiceLaugh(now + 0.46, 180, 0.2, 0.4);   // HAAA
      createVoiceLaugh(now + 0.64, 230, 0.12, 0.3);  // ha
      createVoiceLaugh(now + 0.74, 250, 0.15, 0.35); // ha
      createVoiceLaugh(now + 0.87, 190, 0.22, 0.3);  // haaaa
      createVoiceLaugh(now + 1.07, 210, 0.1, 0.2);   // ha
      createVoiceLaugh(now + 1.15, 170, 0.25, 0.25); // haaaa (fade)
    });
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Natural sorting
const naturalSort = (a, b) => {
  const extractNum = (str) => {
    const match = str.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };
  return extractNum(a.code) - extractNum(b.code);
};

// Censor customer name for privacy on screen
const censorName = (name) => {
  if (!name || name === 'N/A') return name;
  const parts = name.trim().split(/\s+/);
  return parts.map(part => {
    if (part.length <= 2) return part;
    return part[0] + '*'.repeat(part.length - 2) + part[part.length - 1];
  }).join(' ');
};

// Custom text display component
const CustomTextDisplay = ({ text }) => {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
  }, [text]);
  
  if (!text) return null;
  
  return (
    <div className="absolute inset-0 w-full h-full bg-black overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#080808] to-[#0A0A0A]" />
      <div 
        className={`relative z-10 text-center px-8 transition-all duration-1000 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <p className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-relaxed">
          {text}
        </p>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
        <p className="text-[#C9A227]/30 text-xs tracking-[0.4em] uppercase">
          GIÓ PARFUM
        </p>
      </div>
    </div>
  );
};

// Animated perfume showcase display for single product
const PerfumeShowcase = ({ perfume, pricePerMl }) => {
  const [visibleNotes, setVisibleNotes] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  
  // Parse notes into array
  const parseNotes = (notesStr) => {
    if (!notesStr) return [];
    return notesStr
      .split(/[•,]/)
      .map(n => n.trim())
      .filter(n => n.length > 0);
  };
  
  const notesArray = parseNotes(perfume?.notes);
  // Use clean_name (brand censored) for display, fallback to name
  const displayName = perfume?.clean_name || perfume?.name || '';
  
  useEffect(() => {
    setVisibleNotes([]);
    setShowInfo(false);
    
    setTimeout(() => setShowInfo(true), 300);
    
    notesArray.forEach((_, index) => {
      setTimeout(() => {
        setVisibleNotes(prev => [...prev, index]);
      }, 800 + index * 600);
    });
  }, [perfume?.id]);

  if (!perfume) return null;

  return (
    <div className="absolute inset-0 w-full h-full bg-black overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#080808] to-[#0A0A0A]" />
      
      {/* Animated particles */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#C9A227] rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.3,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8">
        {/* Product code - HUGE */}
        <div 
          className={`text-center mb-4 transition-all duration-1000 ${
            showInfo ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
          }`}
        >
          <p className="text-[#C9A227] text-xl tracking-[0.5em] uppercase mb-4">
            GIÓ PARFUM
          </p>
          <h1 className="text-white text-8xl md:text-[12rem] font-bold leading-none">
            {perfume.code}
          </h1>
        </div>

        {/* Perfume name without brand */}
        {displayName && (
          <div 
            className={`mb-8 transition-all duration-1000 delay-200 ${
              showInfo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="text-white/70 text-3xl md:text-5xl font-light tracking-wider uppercase">
              {displayName}
            </p>
          </div>
        )}

        {/* Price per ML */}
        {pricePerMl && (
          <div 
            className={`mb-12 transition-all duration-1000 delay-300 ${
              showInfo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-[#C9A227]/10 border border-[#C9A227]/30 rounded-2xl px-12 py-6">
              <p className="text-[#C9A227]/60 text-lg uppercase tracking-wider mb-2 text-center">
                Prezzo TikTok
              </p>
              <p className="text-[#C9A227] text-5xl md:text-6xl font-bold text-center">
                €{pricePerMl.toFixed(2)}<span className="text-3xl">/ml</span>
              </p>
            </div>
          </div>
        )}

        {/* Fragrance Notes - Animated */}
        {notesArray.length > 0 && (
          <div className="max-w-5xl w-full">
            <p className="text-[#C9A227]/50 text-sm uppercase tracking-[0.3em] text-center mb-6">
              Note Olfattive
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {notesArray.map((note, index) => (
                <span
                  key={index}
                  className={`px-6 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-white text-xl md:text-2xl font-light transition-all duration-700 ${
                    visibleNotes.includes(index)
                      ? 'opacity-100 translate-y-0 scale-100'
                      : 'opacity-0 translate-y-8 scale-90'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {note}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom branding */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
        <p className="text-[#C9A227]/30 text-xs tracking-[0.4em] uppercase">
          GIÓ PARFUM
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
          50% { transform: translateY(-20px) scale(1.5); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

// Smooth animated order display
const OrderDisplay = ({ order, onComplete }) => {
  const [showHeader, setShowHeader] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowHeader(true), 100);

    order.items.forEach((item, index) => {
      setTimeout(() => {
        setVisibleItems(prev => [...prev, index]);
      }, 1500 + index * 2000);
    });

    const animationTime = 1500 + order.items.length * 2000;
    const holdTime = 7000;
    const totalTime = animationTime + holdTime;
    
    setTimeout(() => setFadeOut(true), totalTime - 1000);
    setTimeout(onComplete, totalTime);
  }, [order, onComplete]);

  return (
    <div 
      className={`absolute inset-0 w-full h-full bg-black overflow-hidden transition-opacity duration-1000 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#080808] to-[#0A0A0A]" />
      
      <div className="absolute inset-0">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 bg-[#C9A227] rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.4,
              animation: `float ${4 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 h-full flex">
        <div 
          className={`w-1/3 h-full flex flex-col justify-center items-center p-8 border-r border-[#C9A227]/20 transition-all duration-1000 ${
            showHeader ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'
          }`}
        >
          <div className="text-center">
            <p className="text-[#C9A227] text-sm tracking-[0.4em] uppercase mb-4">
              Nuovo Ordine
            </p>
            <h1 className="text-white text-4xl md:text-5xl font-bold mb-8 leading-tight">
              {censorName(order.customerName)}
            </h1>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#C9A227]/10 rounded-2xl blur-xl" />
              <div className="relative bg-black/50 border border-[#C9A227]/40 rounded-xl px-8 py-6">
                <p className="text-[#C9A227]/60 text-xs uppercase tracking-wider mb-2">Totale</p>
                <p className="text-[#C9A227] text-5xl md:text-6xl font-bold">
                  €{order.total.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 h-full flex flex-col justify-center px-8 py-12 overflow-hidden">
          <div className="space-y-4 max-h-full overflow-y-auto pr-4 scrollbar-hide">
            {order.items.map((item, index) => (
              <div
                key={index}
                className={`transform transition-all duration-700 ease-out ${
                  visibleItems.includes(index)
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-20'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center gap-6 bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
                  <div className="w-20 h-28 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                    <img
                      src={item.image_url}
                      alt={item.perfume_code}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 140"><rect fill="%23C9A227" opacity="0.2" width="100" height="140"/><text x="50" y="70" text-anchor="middle" fill="%23C9A227" font-size="24" font-weight="bold">${item.perfume_code}</text></svg>`;
                      }}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#C9A227] text-3xl font-bold">
                        {item.perfume_code}
                      </span>
                      <Badge className="bg-white/10 text-white border-white/20 text-sm px-3 py-1">
                        {item.format}
                      </Badge>
                    </div>
                    {item.notes && (
                      <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">
                        {item.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
        <p className="text-[#C9A227]/40 text-xs tracking-[0.3em] uppercase">
          GIÓ PARFUM
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-15px) scale(1.2); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

const LivePage = () => {
  const [mode, setMode] = useState(null);
  const [perfumes, setPerfumes] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  
  // External mode state
  const [customerName, setCustomerName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [cart, setCart] = useState([]);
  const [totalPrice, setTotalPrice] = useState("");
  
  // Showcase mode state
  const [showcaseSearchCode, setShowcaseSearchCode] = useState("");
  const [selectedShowcasePerfume, setSelectedShowcasePerfume] = useState(null);
  
  // Text mode state
  const [customTextInput, setCustomTextInput] = useState("");
  const [textDuration, setTextDuration] = useState(10);
  const [customText, setCustomText] = useState(null);
  
  // Internal mode state
  const [pendingOrders, setPendingOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showcasePerfume, setShowcasePerfume] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMirrored, setIsMirrored] = useState(false);
  const containerRef = useRef(null);
  const prevOrdersCountRef = useRef(0);
  
  // Fake revenue state
  const [fakeRevenue, setFakeRevenue] = useState(0);
  const [displayFakeRevenue, setDisplayFakeRevenue] = useState(0);
  const [fakeRevenueInput, setFakeRevenueInput] = useState("");
  const [showFakeRevenue, setShowFakeRevenue] = useState(false);
  const [fakeRevenueFixed, setFakeRevenueFixed] = useState(false);

  // Play sound when new order arrives (only in internal mode)
  useEffect(() => {
    if (mode === 'internal' && pendingOrders.length > prevOrdersCountRef.current) {
      playNotificationSound();
    }
    prevOrdersCountRef.current = pendingOrders.length;
  }, [pendingOrders.length, mode]);

  // Load initial state from API
  const loadLiveState = async () => {
    try {
      const res = await liveAPI.getState();
      setPendingOrders(res.data.orders || []);
      if (res.data.showcase) {
        setShowcasePerfume(res.data.showcase);
      }
      if (res.data.fake_revenue !== undefined) {
        setFakeRevenue(res.data.fake_revenue);
      }
    } catch (error) {
      console.error('Error loading live state:', error);
    }
  };

  useEffect(() => {
    loadPerfumes();
    loadPrices();
    loadLiveState();
  }, []);

  // Polling for internal mode - fetch from API for cross-device sync
  useEffect(() => {
    if (mode !== 'internal') return;
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await liveAPI.getState();
        const newOrders = res.data.orders || [];
        const newShowcase = res.data.showcase;
        const newCustomText = res.data.custom_text;
        const newFakeRevenue = res.data.fake_revenue || 0;
        
        // Update orders if changed
        if (JSON.stringify(newOrders) !== JSON.stringify(pendingOrders)) {
          setPendingOrders(newOrders);
        }
        
        // Update showcase if changed
        if (JSON.stringify(newShowcase) !== JSON.stringify(showcasePerfume)) {
          setShowcasePerfume(newShowcase);
        }
        
        // Update custom text if changed
        if (JSON.stringify(newCustomText) !== JSON.stringify(customText)) {
          setCustomText(newCustomText);
        }
        
        // Update fake revenue if changed
        if (newFakeRevenue !== fakeRevenue) {
          setFakeRevenue(newFakeRevenue);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000); // Increased to 3 seconds to reduce server load
    
    return () => clearInterval(pollInterval);
  }, [mode, showcasePerfume, pendingOrders, customText, fakeRevenue]);

  // Animated counter for fake revenue
  useEffect(() => {
    if (!showFakeRevenue) return;
    const duration = 1500;
    const steps = 60;
    const stepTime = duration / steps;
    const increment = (fakeRevenue - displayFakeRevenue) / steps;
    
    if (Math.abs(fakeRevenue - displayFakeRevenue) < 0.01) return;
    
    let current = displayFakeRevenue;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        setDisplayFakeRevenue(fakeRevenue);
        clearInterval(timer);
      } else {
        setDisplayFakeRevenue(current);
      }
    }, stepTime);
    
    return () => clearInterval(timer);
  }, [fakeRevenue, showFakeRevenue]);

  useEffect(() => {
    // Priority: Order > Showcase
    if (mode === 'internal' && !currentOrder && pendingOrders.length > 0) {
      // Clear showcase when order arrives
      setShowcasePerfume(null);
      const timer = setTimeout(() => {
        setCurrentOrder(pendingOrders[0]);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [mode, currentOrder, pendingOrders]);

  const loadPerfumes = async () => {
    try {
      const res = await perfumeAPI.getAll();
      setPerfumes(res.data);
    } catch (error) {
      toast.error("Errore caricamento profumi");
    } finally {
      setLoading(false);
    }
  };

  const loadPrices = async () => {
    try {
      const res = await pricesAPI.getAll();
      // Convert to map: format -> channel -> price
      const priceMap = {};
      res.data.forEach(p => {
        if (!priceMap[p.format]) priceMap[p.format] = {};
        priceMap[p.format][p.channel] = p.price;
      });
      setPrices(priceMap);
    } catch (error) {
      console.error("Error loading prices:", error);
    }
  };

  // Calculate price per ML for TikTok
  const getTikTokPricePerMl = () => {
    // Get 100ml price for TikTok as base
    const price100ml = prices['100ml']?.['tiktok_shop'];
    if (price100ml) return price100ml / 100;
    return null;
  };

  // Fake revenue handlers
  const handleFakeRevenueNumpad = (value) => {
    if (value === "DEL") {
      setFakeRevenueInput(prev => prev.slice(0, -1));
    } else if (value === "C") {
      setFakeRevenueInput("");
    } else if (value === "." && !fakeRevenueInput.includes(".")) {
      setFakeRevenueInput(prev => prev + ".");
    } else if (value !== ".") {
      setFakeRevenueInput(prev => prev + value);
    }
  };

  const handleAddFakeRevenue = async () => {
    const amount = parseFloat(fakeRevenueInput);
    if (isNaN(amount) || amount === 0) return;
    
    try {
      await liveAPI.addFakeRevenue(amount);
      setFakeRevenueInput("");
      toast.success(`+€${amount.toFixed(2)} aggiunto`);
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleResetFakeRevenue = async () => {
    if (!confirm("Azzerare il fatturato fake?")) return;
    try {
      await liveAPI.resetFakeRevenue();
      setFakeRevenueInput("");
      toast.success("Fatturato azzerato");
    } catch (error) {
      toast.error("Errore");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const matchingPerfumes = searchCode
    ? perfumes
        .filter(p => p.code.toLowerCase().includes(searchCode.toLowerCase()))
        .sort(naturalSort)
        .slice(0, 6)
    : [];

  const showcaseMatchingPerfumes = showcaseSearchCode
    ? perfumes
        .filter(p => p.code.toLowerCase().includes(showcaseSearchCode.toLowerCase()))
        .sort(naturalSort)
        .slice(0, 8)
    : [];

  const handleNumpadClick = (value, target = 'search') => {
    const setter = target === 'showcase' ? setShowcaseSearchCode : setSearchCode;
    if (value === "DEL") {
      setter(prev => prev.slice(0, -1));
    } else if (value === "C") {
      setter("");
    } else if (value !== ".") {
      setter(prev => prev + value);
    }
  };

  const handleAddToCart = (perfume, format) => {
    const imageUrl = perfume.image_url || `https://equivalencia.it/wp-content/uploads/2025/07/${perfume.code}-scaled.jpg`;
    const notes = perfume.notes || null;
    
    setCart(prev => [...prev, {
      perfume_id: perfume.id,
      perfume_code: perfume.code,
      format: format,
      image_url: imageUrl,
      notes: notes,
    }]);
    setSearchCode("");
    toast.success(`${perfume.code} - ${format} aggiunto`);
  };

  const handleRemoveFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendOrder = async () => {
    if (!customerName.trim()) {
      toast.error("Inserisci il nome del cliente");
      return;
    }
    if (cart.length === 0) {
      toast.error("Aggiungi almeno un profumo");
      return;
    }
    if (!totalPrice || parseFloat(totalPrice) <= 0) {
      toast.error("Inserisci il totale pagato");
      return;
    }

    const newOrder = {
      id: Date.now().toString(),
      customerName: customerName.trim(),
      items: cart,
      total: parseFloat(totalPrice),
      createdAt: new Date().toISOString(),
    };

    try {
      await liveAPI.addOrder(newOrder);
      setPendingOrders(prev => [...prev, newOrder]);
      
      setCustomerName("");
      setCart([]);
      setTotalPrice("");
      setSearchCode("");
      
      toast.success("Ordine inviato!");
    } catch (error) {
      toast.error("Errore invio ordine");
    }
  };

  const handleSelectShowcasePerfume = async (perfume) => {
    const showcaseData = {
      id: perfume.id,
      code: perfume.code,
      name: perfume.name,
      clean_name: perfume.clean_name || perfume.name,  // Use clean_name for display
      notes: perfume.notes || null,
      image_url: perfume.image_url || `https://equivalencia.it/wp-content/uploads/2025/07/${perfume.code}-scaled.jpg`,
    };
    
    try {
      await liveAPI.setShowcase(showcaseData);
      setSelectedShowcasePerfume(showcaseData);
      setShowcaseSearchCode("");
      toast.success(`${perfume.code} inviato allo schermo!`);
    } catch (error) {
      toast.error("Errore invio profumo");
    }
  };

  const handleOrderComplete = async () => {
    const completedOrder = pendingOrders[0];
    if (completedOrder) {
      try {
        await liveAPI.removeOrder(completedOrder.id);
      } catch (error) {
        console.error('Error removing order:', error);
      }
    }
    setPendingOrders(prev => prev.slice(1));
    setCurrentOrder(null);
  };

  const handleSendText = async () => {
    if (!customTextInput.trim()) {
      toast.error("Inserisci un testo");
      return;
    }
    try {
      await liveAPI.setText(customTextInput.trim(), textDuration);
      setCustomTextInput("");
      toast.success(`Testo inviato per ${textDuration}s!`);
    } catch (error) {
      toast.error("Errore invio testo");
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Mode selection
  if (!mode) {
    return (
      <div data-testid="live-page" className="animate-fadeIn">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">Live</h1>
          <p className="text-gray-500">Visualizzazione ordini in tempo reale</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
          <button
            onClick={() => setMode('external')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-[#C9A227] rounded-2xl transition-all hover:shadow-lg"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center transition-colors">
                <Smartphone className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Esterno</h2>
              <p className="text-gray-500 text-center text-sm">Crea ordini con carrello</p>
            </div>
          </button>

          <button
            onClick={() => setMode('showcase')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-[#C9A227] rounded-2xl transition-all hover:shadow-lg"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-amber-100 group-hover:bg-amber-200 flex items-center justify-center transition-colors">
                <Sparkles className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Mostra Equivalenti</h2>
              <p className="text-gray-500 text-center text-sm">Mostra singolo profumo</p>
            </div>
          </button>

          <button
            onClick={() => setMode('internal')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-[#C9A227] rounded-2xl transition-all hover:shadow-lg relative"
          >
            {pendingOrders.length > 0 && (
              <Badge className="absolute top-4 right-4 bg-red-500 text-white">
                {pendingOrders.length} in coda
              </Badge>
            )}
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-purple-100 group-hover:bg-purple-200 flex items-center justify-center transition-colors">
                <Monitor className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Interno</h2>
              <p className="text-gray-500 text-center text-sm">Display per TV/monitor</p>
            </div>
          </button>

          <button
            onClick={() => setMode('text')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-[#C9A227] rounded-2xl transition-all hover:shadow-lg"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-green-100 group-hover:bg-green-200 flex items-center justify-center transition-colors">
                <Type className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Testo</h2>
              <p className="text-gray-500 text-center text-sm">Invia messaggio a schermo</p>
            </div>
          </button>

          <button
            onClick={() => setMode('sounds')}
            className="group p-8 bg-white border-2 border-gray-200 hover:border-[#C9A227] rounded-2xl transition-all hover:shadow-lg"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-pink-100 group-hover:bg-pink-200 flex items-center justify-center transition-colors">
                <Volume2 className="w-10 h-10 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Suoni</h2>
              <p className="text-gray-500 text-center text-sm">Campanella e risate</p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Sounds mode - Play sounds on the internal display
  if (mode === 'sounds') {
    return (
      <div data-testid="live-sounds" className="animate-fadeIn">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif text-gray-900 mb-1">Suoni</h1>
            <p className="text-gray-500 text-sm">Riproduci suoni sullo schermo interno</p>
          </div>
          <Button variant="outline" onClick={() => setMode(null)}>
            <X className="w-4 h-4 mr-2" /> Indietro
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
          <button
            onClick={() => {
              playBellSound();
              toast.success("🔔 Campanella!");
            }}
            className="group p-10 bg-white border-2 border-gray-200 hover:border-amber-400 rounded-2xl transition-all hover:shadow-xl active:scale-95"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-24 h-24 rounded-full bg-amber-100 group-hover:bg-amber-200 flex items-center justify-center transition-all group-hover:scale-110">
                <Bell className="w-12 h-12 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Campanella</h2>
              <p className="text-gray-500 text-center">Suono celebrativo per nuovi ordini</p>
            </div>
          </button>

          <button
            onClick={() => {
              playLaughterSound();
              toast.success("😂 Risate!");
            }}
            className="group p-10 bg-white border-2 border-gray-200 hover:border-pink-400 rounded-2xl transition-all hover:shadow-xl active:scale-95"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-24 h-24 rounded-full bg-pink-100 group-hover:bg-pink-200 flex items-center justify-center transition-all group-hover:scale-110">
                <Laugh className="w-12 h-12 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Risate</h2>
              <p className="text-gray-500 text-center">Effetto sonoro divertente</p>
            </div>
          </button>
        </div>

        <p className="text-center text-gray-400 mt-8 text-sm">
          I suoni vengono riprodotti sul dispositivo corrente
        </p>
      </div>
    );
  }

  // Text mode - Send custom text to display
  if (mode === 'text') {
    return (
      <div data-testid="live-text" className="animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-serif text-gray-900 mb-1">Invia Testo</h1>
            <p className="text-gray-500 text-sm">Scrivi un messaggio da mostrare su schermo</p>
          </div>
          <Button variant="outline" onClick={() => setMode(null)}>
            <X className="w-4 h-4 mr-2" /> Indietro
          </Button>
        </div>

        <div className="max-w-xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Messaggio</label>
              <textarea
                value={customTextInput}
                onChange={(e) => setCustomTextInput(e.target.value)}
                placeholder="Scrivi il testo da mostrare..."
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A227] resize-none"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Durata (secondi)</label>
              <div className="flex gap-2 flex-wrap">
                {TEXT_DURATIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setTextDuration(d)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      textDuration === d
                        ? 'bg-[#C9A227] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {d}s
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleSendText}
              disabled={!customTextInput.trim()}
              className="w-full h-12 bg-[#C9A227] hover:bg-[#D4AF37] text-white font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Invia a Schermo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Showcase mode - Select perfume to display
  if (mode === 'showcase') {
    return (
      <div data-testid="live-showcase" className="animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-serif text-gray-900 mb-1">Mostra Equivalenti</h1>
            <p className="text-gray-500 text-sm">Seleziona un profumo da mostrare su schermo</p>
          </div>
          <Button variant="outline" onClick={() => setMode(null)}>
            <X className="w-4 h-4 mr-2" /> Indietro
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left - Numpad for perfume code */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="mb-3">
              <p className="text-xs text-gray-500 uppercase mb-2">Codice Profumo</p>
              <div className="bg-gray-100 rounded-xl p-3 text-center">
                <span className="text-3xl font-bold text-gray-900">{showcaseSearchCode || "..."}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {["7", "8", "9", "4", "5", "6", "1", "2", "3", "C", "0", "DEL"].map((key) => (
                <button
                  key={key}
                  onClick={() => handleNumpadClick(key, 'showcase')}
                  className={`h-12 rounded-xl text-xl font-bold transition-all active:scale-95 ${
                    key === "DEL"
                      ? "bg-red-100 text-red-600 hover:bg-red-200"
                      : key === "C"
                      ? "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  }`}
                >
                  {key === "DEL" ? <Delete className="w-5 h-5 mx-auto" /> : key}
                </button>
              ))}
            </div>

            {selectedShowcasePerfume && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-700 text-sm font-medium">
                  ✓ Su schermo: <span className="font-bold">{selectedShowcasePerfume.code}</span>
                </p>
              </div>
            )}
          </div>

          {/* Center - Results */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">RISULTATI - Clicca per mostrare</h3>
            
            {showcaseSearchCode ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {showcaseMatchingPerfumes.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleSelectShowcasePerfume(p)}
                    className="w-full p-3 bg-gray-50 hover:bg-[#C9A227]/10 rounded-xl border border-gray-100 hover:border-[#C9A227] transition-all text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#C9A227]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-[#C9A227] font-bold text-sm">{p.code}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-medium truncate text-sm">{p.name}</p>
                        {p.notes && (
                          <p className="text-gray-500 text-xs truncate">{p.notes}</p>
                        )}
                      </div>
                      <Sparkles className="w-4 h-4 text-[#C9A227]" />
                    </div>
                  </button>
                ))}
                {showcaseMatchingPerfumes.length === 0 && (
                  <p className="text-gray-400 text-center py-6 text-sm">Nessun risultato</p>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-10 text-sm">Digita il codice del profumo...</p>
            )}
          </div>

          {/* Right - Fake Revenue Control */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white text-sm flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#C9A227]" />
                FATTURATO FAKE
              </h3>
              <Badge className={`${showFakeRevenue ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {showFakeRevenue ? 'ATTIVO' : 'OFF'}
              </Badge>
            </div>
            
            {/* Current fake revenue display */}
            <div className="bg-black/50 rounded-xl p-4 mb-3 text-center border border-[#C9A227]/30">
              <p className="text-[#C9A227] text-xs uppercase tracking-wider mb-1">Totale Attuale</p>
              <p className="text-white text-3xl font-bold tabular-nums">{formatCurrency(fakeRevenue)}</p>
            </div>

            {/* Input display */}
            <div className="bg-white/10 rounded-lg p-2 mb-3 text-center">
              <p className="text-gray-400 text-xs mb-1">Da aggiungere</p>
              <p className="text-[#C9A227] text-2xl font-bold">€{fakeRevenueInput || "0"}</p>
            </div>

            {/* Numpad */}
            <div className="grid grid-cols-4 gap-1.5 mb-3">
              {["1", "2", "3", "C", "4", "5", "6", "DEL", "7", "8", "9", ".", "00", "0", "000"].map((key) => (
                <button
                  key={key}
                  onClick={() => {
                    if (key === "00") setFakeRevenueInput(prev => prev + "00");
                    else if (key === "000") setFakeRevenueInput(prev => prev + "000");
                    else handleFakeRevenueNumpad(key);
                  }}
                  className={`h-10 rounded-lg font-bold text-sm transition-all active:scale-95 ${
                    key === "C" ? "bg-red-500/30 text-red-400 hover:bg-red-500/50" :
                    key === "DEL" ? "bg-orange-500/30 text-orange-400 hover:bg-orange-500/50" :
                    "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  {key === "DEL" ? "←" : key}
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Button
                onClick={handleAddFakeRevenue}
                disabled={!fakeRevenueInput}
                className="bg-green-600 hover:bg-green-700 text-white h-10"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
              <Button
                onClick={handleResetFakeRevenue}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20 h-10"
                size="sm"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Azzera
              </Button>
            </div>

            {/* Toggle visibility */}
            <Button
              onClick={() => {
                const newShowFakeRevenue = !showFakeRevenue;
                setShowFakeRevenue(newShowFakeRevenue);
                if (newShowFakeRevenue) {
                  setDisplayFakeRevenue(fakeRevenue);
                }
              }}
              variant="outline"
              className={`w-full h-10 ${showFakeRevenue ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-white/10 border-white/20 text-white'}`}
              size="sm"
            >
              {showFakeRevenue ? (
                <><Lock className="w-4 h-4 mr-2" /> Visibile su Schermo</>
              ) : (
                <><Unlock className="w-4 h-4 mr-2" /> Mostra su Schermo</>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // External mode
  if (mode === 'external') {
    return (
      <div data-testid="live-external" className="animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-serif text-gray-900 mb-1">Crea Ordine Live</h1>
            <p className="text-gray-500 text-sm">Seleziona profumi e formato</p>
          </div>
          <Button variant="outline" onClick={() => setMode(null)}>
            <X className="w-4 h-4 mr-2" /> Indietro
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left - Customer & Search Results */}
          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                <User className="w-4 h-4 text-[#C9A227]" />
                NOME CLIENTE
              </h3>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Mario Rossi"
                className="bg-gray-50 border-gray-200"
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 text-sm mb-2">RISULTATI - Clicca formato</h3>
              {searchCode ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {matchingPerfumes.map((p) => (
                    <div
                      key={p.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      <p className="text-[#C9A227] font-bold mb-2">{p.code}</p>
                      <div className="flex flex-wrap gap-1">
                        {FORMATS.map((fmt) => (
                          <button
                            key={fmt}
                            onClick={() => handleAddToCart(p, fmt)}
                            className="px-3 py-1.5 bg-white border border-gray-200 hover:border-[#C9A227] hover:bg-[#C9A227]/10 rounded-lg text-sm font-medium transition-colors"
                          >
                            {fmt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {matchingPerfumes.length === 0 && (
                    <p className="text-gray-400 text-center py-4 text-sm">Nessun risultato</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-6">Digita il codice...</p>
              )}
            </div>
          </div>

          {/* Middle - Numpad */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="mb-3">
              <p className="text-xs text-gray-500 uppercase mb-2">Codice Profumo</p>
              <div className="bg-gray-100 rounded-xl p-3 text-right">
                <span className="text-3xl font-bold text-gray-900">{searchCode || "..."}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {["7", "8", "9", "4", "5", "6", "1", "2", "3", "C", "0", "DEL"].map((key) => (
                <button
                  key={key}
                  onClick={() => handleNumpadClick(key)}
                  className={`h-12 rounded-xl text-xl font-bold transition-all active:scale-95 ${
                    key === "DEL"
                      ? "bg-red-100 text-red-600"
                      : key === "C"
                      ? "bg-gray-200 text-gray-600"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  }`}
                >
                  {key === "DEL" ? <Delete className="w-5 h-5 mx-auto" /> : key}
                </button>
              ))}
            </div>

            <div className="mb-4">
              <p className="text-xs text-gray-500 uppercase mb-2">Totale Pagato €</p>
              <Input
                type="number"
                step="0.01"
                value={totalPrice}
                onChange={(e) => setTotalPrice(e.target.value)}
                placeholder="0.00"
                className="text-2xl font-bold text-center bg-gray-900 text-white border-0"
              />
            </div>

            <Button
              onClick={handleSendOrder}
              disabled={!customerName || cart.length === 0 || !totalPrice}
              className="w-full h-12 bg-[#C9A227] text-white hover:bg-[#D4AF37] rounded-xl font-semibold"
            >
              <Send className="w-5 h-5 mr-2" />
              Invia Ordine
            </Button>
          </div>

          {/* Right - Cart */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#C9A227]" />
                CARRELLO
              </h3>
              <Badge className="bg-[#C9A227]/10 text-[#C9A227]">{cart.length}</Badge>
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {cart.length === 0 ? (
                <p className="text-gray-400 text-center py-8 text-sm">Carrello vuoto</p>
              ) : (
                cart.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A227] font-bold">{item.perfume_code}</span>
                      <Badge className="bg-gray-200 text-gray-700 text-xs">
                        {item.format}
                      </Badge>
                    </div>
                    <button
                      onClick={() => handleRemoveFromCart(index)}
                      className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Internal mode
  if (mode === 'internal') {
    const pricePerMl = getTikTokPricePerMl();
    
    return (
      <div 
        ref={containerRef}
        data-testid="live-internal" 
        className={`${isFullscreen ? 'fixed inset-0 z-50' : 'relative min-h-[80vh]'} bg-black`}
      >
        {/* Inner wrapper for mirror transform - contains content only */}
        <div 
          className="absolute inset-0 w-full h-full"
          style={{ transform: isMirrored ? 'scaleX(-1)' : 'none' }}
        >
          {/* Priority 1: Order display */}
          {currentOrder && (
            <OrderDisplay order={currentOrder} onComplete={handleOrderComplete} />
          )}

          {/* Priority 2: Custom text */}
          {!currentOrder && customText && (
            <CustomTextDisplay text={customText.text} />
          )}

          {/* Priority 3: Showcase perfume */}
          {!currentOrder && !customText && showcasePerfume && (
            <PerfumeShowcase perfume={showcasePerfume} pricePerMl={pricePerMl} />
          )}

          {/* Waiting screen (nothing to show) */}
          {!currentOrder && !customText && !showcasePerfume && (
            <div className="absolute inset-0 bg-black flex flex-col items-center justify-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0A0A0A] to-black" />
              
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.03]">
                <h1 className="text-[20vw] font-bold text-white whitespace-nowrap">
                  GIÓ PARFUM
                </h1>
              </div>

              <div className="relative z-10 text-center">
                <h1 className="text-white text-5xl md:text-7xl font-bold mb-4">
                  GIÓ PARFUM
                </h1>
                <p className="text-[#C9A227] text-lg tracking-[0.3em] uppercase mb-8">
                  PROFUMI
                </p>
                
                {pendingOrders.length > 0 ? (
                  <div className="animate-pulse">
                    <p className="text-white text-xl mb-2">
                      {pendingOrders.length} ordini in coda
                    </p>
                    <p className="text-gray-400">Prossimo ordine...</p>
                  </div>
                ) : (
                  <p className="text-gray-500">In attesa di nuovi ordini...</p>
                )}
              </div>
            </div>
          )}

          {/* ALWAYS VISIBLE: Fake Revenue Overlay - LEFT side (mirrors to right top) */}
          {showFakeRevenue && (
            <div 
              className="absolute top-6 left-6 z-[70] bg-black/80 backdrop-blur-md rounded-2xl p-4 md:p-6 border border-[#C9A227]/30"
              style={{ 
                boxShadow: '0 0 40px rgba(201, 162, 39, 0.2)'
              }}
            >
              <p className="text-[#C9A227] text-xs md:text-sm font-semibold tracking-[0.2em] uppercase mb-2">
                Fatturato Oggi
              </p>
              <h2 
                className="text-white text-3xl md:text-5xl lg:text-6xl font-bold tabular-nums"
                style={{ textShadow: '0 0 20px rgba(201, 162, 39, 0.3)' }}
              >
                {formatCurrency(displayFakeRevenue)}
              </h2>
            </div>
          )}
        </div>

        {/* Control buttons - OUTSIDE mirror wrapper, always readable */}
        <div className="absolute top-4 right-4 flex gap-2 z-[80]">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (currentOrder || showcasePerfume || customText) {
                setCurrentOrder(null);
                setShowcasePerfume(null);
                setCustomText(null);
                try {
                  await liveAPI.clearShowcase();
                  await liveAPI.clearText();
                } catch (e) {}
              } else {
                setMode(null);
              }
            }}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <X className="w-4 h-4 mr-1" /> {currentOrder || showcasePerfume || customText ? 'Chiudi' : 'Esci'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newShowFakeRevenue = !showFakeRevenue;
              setShowFakeRevenue(newShowFakeRevenue);
              if (newShowFakeRevenue) {
                setDisplayFakeRevenue(fakeRevenue);
              }
            }}
            className={`border-white/20 text-white hover:bg-white/20 ${showFakeRevenue ? 'bg-green-500/30' : 'bg-white/10'}`}
            title={showFakeRevenue ? "Nascondi Fatturato" : "Mostra Fatturato"}
          >
            <DollarSign className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMirrored(!isMirrored)}
            className={`border-white/20 text-white hover:bg-white/20 ${isMirrored ? 'bg-[#C9A227]/30' : 'bg-white/10'}`}
            title="Modalità Specchio"
          >
            <FlipHorizontal2 className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default LivePage;
